import { Component } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { PremiseService } from '../_services/premise.service';
import { ToastrService } from 'ngx-toastr';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faCircleArrowLeft, faHomeAlt, faSearch, faPaperPlane, faX } from '@fortawesome/free-solid-svg-icons';
import { Complaint, ComplaintService } from '../_services';
import { PremiseAssociation } from '../_models/premise_association';
import { forkJoin } from 'rxjs';
import { ComplaintStatus } from '../_models/complaint-status';
import { getComplaintStatusLabel } from '../_models/complaint-status';
import { environment } from '../../environments/environment';
import { MessageResponse } from '../_models/responses';

@Component({
  selector: 'app-complaints',
  templateUrl: './complaints.component.html',
  styleUrls: ['./complaints.component.css'],
  standalone: false
})
export class ComplaintsComponent {
  faCircleArrowLeft = faCircleArrowLeft;
  faHomeAlt = faHomeAlt;
  faSearch = faSearch;
  faPaperPlane = faPaperPlane;
  faX = faX;

  loading = false;
  fetching = false;
  searchQuery = '';
  premiseAssociations: PremiseAssociation[] = [];
  activeTab: 'complainant' | 'complainee' = 'complainant';
  isCreateMode = false;
  submitComplaintData = false;

  myComplaints: Complaint[] = [];
  complaintReports: Complaint[] = [];
  createComplaintForm!: FormGroup;

  getComplaintStatusLabel = getComplaintStatusLabel;
  ComplaintStatus = ComplaintStatus;

  constructor(
    private toastr: ToastrService,
    private premiseService: PremiseService,
    private complaintService: ComplaintService,
    private formBuilder: FormBuilder,
    private _location: Location,
    private router: Router
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loading = true;

    this.premiseService.fetchUserAssociation().subscribe({
      next: (associations) => {
        this.premiseAssociations = associations || [];

        if (this.premiseAssociations.length === 0) {
          this.loading = false;
          this.router.navigate(['/premises/noassociation']);
          return;
        }

        this.loadComplaints();
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.toastr.error('Something went wrong. Failed to fetch premise associations.', 'Error');
        console.log('Association fetch error: ', error);
      },
    });
  }

  goBack() {
    this._location.back();
  }

  goHome(): void {
    this.router.navigate(['/home']);
  }

  setActiveTab(tab: 'complainant' | 'complainee'): void {
    this.activeTab = tab;
  }

  onCreateComplaint(): void {
    this.isCreateMode = true;
    const firstAssociation = this.premiseAssociations[0];
    if (firstAssociation) {
      this.createComplaintForm.patchValue({
        complainant_section_strata_lotno: String(firstAssociation.unit_number),
        propertyId: firstAssociation.propertyId,
        date_submission: this.getTodayDateString(),
      });
    }
  }

  onCancelCreate(): void {
    this.isCreateMode = false;
    this.submitComplaintData = false;
    this.createComplaintForm.reset();
  }

  submitCreateComplaint(): void {
    this.submitComplaintData = true;
    this.fetching = true;

    if (!this.createComplaintForm.valid) {
      this.fetching = false;
      return;
    }

    const formdata = this.createComplaintForm.value;
    const payload = {
      name_complainant: formdata.name_complainant,
      address_complainant: formdata.address_complainant,
      complainant_email: formdata.complainant_email,
      complainant_type: formdata.complainant_type,
      complainant_section_strata_lotno: formdata.complainant_section_strata_lotno,
      name_complainee: formdata.name_complainee,
      address_complainee: formdata.address_complainee,
      complainee_email: formdata.complainee_email,
      complainee_section_strata_lotno: formdata.complainee_section_strata_lotno,
      grounds_complaint: formdata.grounds_complaint,
      status: ComplaintStatus.Capture,
      date_submission: formdata.date_submission,
      propertyId: Number(formdata.propertyId),
    };

    this.complaintService.createComplaint(payload).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        this.submitComplaintData = false;
        this.isCreateMode = false;
        this.createComplaintForm.reset();
        this.loadComplaints();
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to create complaint.', 'Error');
        if (!environment.production) {
          console.log('Complaint creation failed: ', error);
        }
      },
    });
  }

  get fcf() {
    return this.createComplaintForm.controls;
  }

  get filteredMyComplaints(): Complaint[] {
    return this.filterComplaints(this.myComplaints);
  }

  get filteredComplaintReports(): Complaint[] {
    return this.filterComplaints(this.complaintReports);
  }

  private filterComplaints(complaints: Complaint[]): Complaint[] {
    const query = this.searchQuery.trim().toLowerCase();

    if (!query) {
      return complaints;
    }

    return complaints.filter((complaint) => {
      const searchable = [
        complaint.name_complainant,
        complaint.name_complainee,
        complaint.grounds_complaint,
        complaint.complainant_section_strata_lotno,
        complaint.complainee_section_strata_lotno,
        this.getComplaintStatusLabel(complaint.status),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(query);
    });
  }

  private deduplicateComplaintsById(complaints: Complaint[]): Complaint[] {
    const seen = new Set<number>();
    return (complaints || []).filter((complaint) => {
      if (!complaint.id) {
        return true;
      }

      if (seen.has(complaint.id)) {
        return false;
      }

      seen.add(complaint.id);
      return true;
    });
  }

  private initializeForm(): void {
    this.createComplaintForm = this.formBuilder.group({
      name_complainant: ['', Validators.required],
      address_complainant: ['', Validators.required],
      complainant_email: ['', [Validators.required, Validators.email]],
      complainant_type: ['', Validators.required],
      complainant_section_strata_lotno: ['', Validators.required],
      name_complainee: ['', Validators.required],
      address_complainee: ['', Validators.required],
      complainee_email: ['', [Validators.required, Validators.email]],
      complainee_section_strata_lotno: ['', Validators.required],
      grounds_complaint: ['', Validators.required],
      date_submission: ['', Validators.required],
      propertyId: ['', Validators.required],
    });
  }

  private loadComplaints(): void {
    this.loading = true;

    const complainantRequests = this.premiseAssociations.map((association) =>
      this.complaintService.getComplaintsByComplainantLotNo(
        association.propertyId,
        String(association.unit_number)
      )
    );

    const complaineeRequests = this.premiseAssociations.map((association) =>
      this.complaintService.getComplaintsByComplaineeLotNo(
        association.propertyId,
        String(association.unit_number)
      )
    );

    forkJoin({
      complainantGroups: forkJoin(complainantRequests),
      complaineeGroups: forkJoin(complaineeRequests),
    }).subscribe({
      next: ({ complainantGroups, complaineeGroups }) => {
        this.myComplaints = this.deduplicateComplaintsById(complainantGroups.flat());
        this.complaintReports = this.deduplicateComplaintsById(complaineeGroups.flat());
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.toastr.error('Failed to retrieve complaints.', 'Error');
        console.log('Complaint fetch error: ', error);
      },
    });
  }

  private getTodayDateString(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = `${today.getMonth() + 1}`.padStart(2, '0');
    const day = `${today.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
